import { Auth0Client, User, createAuth0Client } from '@auth0/auth0-spa-js';

import { AuthProvider, AuthRedirectResult } from './types';

import { AuthProviderType } from '@/features/auth/types/enums/auth-provider-type';
import { AuthSession } from '@/features/auth/types/interfaces/auth-session';

const getAuth0Config = () => {
  const fallbackRedirectUri = window.location.origin;
  const redirectUri = import.meta.env.VITE_AUTH0_REDIRECT_URI?.trim() || fallbackRedirectUri;
  const logoutUri = import.meta.env.VITE_AUTH0_LOGOUT_URI?.trim() || window.location.origin;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE?.trim() || '';

  return {
    clientId: import.meta.env.VITE_AUTH0_CLIENT_ID?.trim() ?? '',
    domain: import.meta.env.VITE_AUTH0_DOMAIN?.trim() ?? '',
    audience,
    redirectUri,
    logoutUri,
  };
};

const isAuth0RedirectCallback = () => {
  const params = new URLSearchParams(window.location.search);

  return params.has('code') && params.has('state');
};

const mapAuth0User = (user: User | undefined) => {
  if (!user) {
    return null;
  }

  return {
    id: user.sub ?? user.email ?? 'auth0-user',
    email: user.email ?? null,
    name: user.name ?? user.email ?? null,
  };
};

class Auth0AuthProvider implements AuthProvider {
  readonly type = AuthProviderType.AUTH0;

  private client: Promise<Auth0Client> | null = null;

  private getClient = async () => {
    if (!this.client) {
      const { clientId, domain, audience, redirectUri } = getAuth0Config();

      this.client = createAuth0Client({
        clientId,
        domain,
        cacheLocation: 'localstorage',
        authorizationParams: {
          redirect_uri: redirectUri,
          ...(audience ? { audience } : {}),
        },
      });
    }

    const client = this.client;

    if (!client) {
      throw new Error('Unable to initialize Auth0 client.');
    }

    return client;
  };

  loginWithAuth0 = async (returnTo = '/') => {
    const client = await this.getClient();

    await client.loginWithRedirect({
      appState: {
        returnTo,
      },
    });
  };

  handleRedirectCallback = async (): Promise<AuthRedirectResult | null> => {
    if (!isAuth0RedirectCallback()) {
      return null;
    }

    const client = await this.getClient();
    const response = await client.handleRedirectCallback();

    window.history.replaceState(
      {},
      document.title,
      `${window.location.pathname}${window.location.hash}`,
    );

    return {
      returnTo: typeof response?.appState?.returnTo === 'string' ? response.appState.returnTo : '/',
    };
  };

  getAccessToken = async () => {
    const client = await this.getClient();
    const isAuthenticated = await client.isAuthenticated();

    if (!isAuthenticated) {
      return null;
    }

    try {
      return await client.getTokenSilently();
    } catch {
      return null;
    }
  };

  getSession = async (): Promise<AuthSession | null> => {
    const token = await this.getAccessToken();

    if (!token) {
      return null;
    }

    const client = await this.getClient();
    const user = mapAuth0User(await client.getUser());

    if (!user) {
      return null;
    }

    return {
      access_token: token,
      provider: AuthProviderType.AUTH0,
      user,
    };
  };

  logout = async () => {
    const { logoutUri } = getAuth0Config();
    const client = await this.getClient();

    await client.logout({
      logoutParams: {
        returnTo: logoutUri,
      },
    });
  };
}

export const auth0AuthProvider = new Auth0AuthProvider();
